import { IEmployeeDataSave } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/Forms/OrganizationStructureForm.models";
import { OrganizationStructureApiClient } from "src/Pages/Company/OrganizationStructure/apiClient/OrganizationStructureApiClient";

export const fetchUserData = async (
  companyId: string,
  employeeId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/employees/${employeeId}/organization-structure-relations`,
    {
      signal,
    }
  );
  return data;
};

export const fetchLocalizationsData = async (
  companyId: string,
  structureId: string,
  {
    signal,
    cursor,
    size = 20,
    filters = "",
  }: {
    signal?: AbortSignal;
    cursor?: string | null;
    size?: number;
    filters?: string | null;
  }
) => {
  const params: { [key: string]: any } = {
    size,
  };

  if (cursor) {
    params.cursor = cursor;
  }

  if (filters) {
    params.filters = filters;
  }

  const { data } = await OrganizationStructureApiClient.get(
    `/v2/companies/${companyId}/structures/${structureId}/localizations`,
    {
      params,
      signal,
    }
  );
  return data;
};

export const fetchPositionsData = async (
  companyId: string,
  structureId: string,
  {
    signal,
    cursor,
    size = 20,
    filters,
  }: {
    signal?: AbortSignal;
    cursor?: string | null;
    size?: number;
    filters?: string | null;
  }
) => {
  const params: { [key: string]: any } = {
    size,
  };

  if (cursor) {
    params.cursor = cursor;
  }

  if (filters) {
    params.filters = filters;
  }

  const { data } = await OrganizationStructureApiClient.get(
    `/v2/companies/${companyId}/structures/${structureId}/positions`,
    {
      params,
      signal,
    }
  );
  return data;
};

export const fetchSupervisorsData = async (
  companyId: string,
  employeeId: string,
  {
    signal,
    nextCursor,
    query,
  }: {
    signal: AbortSignal | undefined;
    nextCursor?: string | null | undefined | unknown;
    query?: string | null;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/employees/${employeeId}/available-supervisors`,
    {
      params: {
        cursor: nextCursor,
        filters: query,
      },
      signal,
    }
  );
  return data;
};

export const fetchStructureData = async (
  companyId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures`,
    {
      signal,
    }
  );
  return data;
};

export const saveOrganizationStructureForm = async (
  companyId: string,
  employeeId: string,
  formData: IEmployeeDataSave
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/employees/${employeeId}/organization-structure-relations`,
    formData
  );
  return data;
};

export const fetchAreasData = async (
  companyId: string,
  structureId: string,
  {
    signal,
    nextCursor,
    query,
  }: {
    signal: AbortSignal | undefined;
    nextCursor?: string | null | undefined | unknown;
    query?: string | null;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/references`,
    {
      params: {
        cursor: nextCursor,
        filters: query,
      },
      signal,
    }
  );
  return data;
};

export const fetchAvailableParents = async (
  companyId: string,
  structureId: string,
  areaId: string,
  {
    signal,
    nextCursor,
    query,
  }: {
    signal: AbortSignal | undefined;
    nextCursor?: string | null | undefined | unknown;
    query?: string | null;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/areas/${areaId}/available-parents`,
    {
      params: {
        cursor: nextCursor,
        filters: query,
      },
      signal,
    }
  );
  return data;
};

export interface PotentialArea {
  areaId: string;
  name: string;
  givenEmployeeCanInheritManager: boolean;
  managedByGivenEmployee: boolean;
}

export interface PotentialAreasResponse {
  content: PotentialArea[];
  size: number;
  nextCursor: string | null;
  totalElements: number;
}

export const fetchPotentialAreasToMembership = async (
  companyId: string,
  employeeId: string,
  {
    signal,
    nextCursor,
    query,
  }: {
    signal: AbortSignal | undefined;
    nextCursor?: string | null | undefined | unknown;
    query?: string | null;
  }
): Promise<PotentialAreasResponse> => {
  const { data } =
    await OrganizationStructureApiClient.get<PotentialAreasResponse>(
      `/v1/companies/${companyId}/employees/${employeeId}/potential-areas-to-membership`,
      {
        params: {
          cursor: nextCursor,
          filters: query,
        },
        signal,
      }
    );
  return data;
};

export interface PotentialSupervisor {
  employeeId: string;
  name: string;
  fk: string;
  canBeSupervisor: boolean;
}

export interface PotentialSupervisorsResponse {
  content: PotentialSupervisor[];
  size: number;
  nextCursor: string | null;
  totalElements: number;
}

export const fetchPotentialSupervisors = async (
  companyId: string,
  employeeId: string,
  {
    signal,
    nextCursor,
    query,
  }: {
    signal: AbortSignal | undefined;
    nextCursor?: string | null | undefined | unknown;
    query?: string | null;
  }
): Promise<PotentialSupervisorsResponse> => {
  const { data } =
    await OrganizationStructureApiClient.get<PotentialSupervisorsResponse>(
      `/v1/companies/${companyId}/employees/${employeeId}/potential-supervisors`,
      {
        params: {
          cursor: nextCursor,
          filters: query,
        },
        signal,
      }
    );
  return data;
};
